import React, { useState, useRef } from 'react';
import { AlignLeft, AlignCenter, AlignRight, Download } from 'lucide-react';
import './styles.css';

const ImageEditor = () => {
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [logo, setLogo] = useState(null);
  const [text, setText] = useState('');
  const [fontSize, setFontSize] = useState(24);
  const [textColor, setTextColor] = useState('#FFFFFF');
  const [textAlign, setTextAlign] = useState('center');
  const [gradientColor, setGradientColor] = useState('#000000');
  const [gradientOpacity, setGradientOpacity] = useState(0.5);
  const [logoSize, setLogoSize] = useState(50);
  const [logoPosition, setLogoPosition] = useState({ x: 20, y: 20 });

  const fileInputRef = useRef(null);
  const logoInputRef = useRef(null);
  const editorRef = useRef(null);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => setBackgroundImage(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => setLogo(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const moveLogoPosition = (direction) => {
    const step = 10;
    const positions = {
      up: { x: 0, y: -step },
      down: { x: 0, y: step },
      left: { x: -step, y: 0 },
      right: { x: step, y: 0 },
    };
    
    setLogoPosition(prev => ({
      x: prev.x + positions[direction].x,
      y: prev.y + positions[direction].y
    }));
  };

  const handleDownload = async () => {
    if (!editorRef.current) return;

    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(editorRef.current);
      const dataUrl = canvas.toDataURL('image/png');
      
      const link = document.createElement('a');
      link.download = 'edited-image.png';
      link.href = dataUrl;
      link.click();
    } catch (error) {
      console.error('Error downloading image:', error);
    }
  };

  return (
    <div className="editor-container">
      <div className="editor-controls">
        {/* Image Upload */}
        <div className="control-section">
          <button 
            onClick={() => fileInputRef.current.click()}
            className="upload-button"
          >
            Upload Background Image
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            className="hidden"
          />
        </div>

        {/* Logo Upload */}
        <div className="control-section">
          <button 
            onClick={() => logoInputRef.current.click()}
            className="upload-button"
          >
            Upload Logo
          </button>
          <input
            ref={logoInputRef}
            type="file"
            accept="image/*"
            onChange={handleLogoUpload}
            className="hidden"
          />
          
          {logo && (
            <div className="logo-controls">
              <div className="slider-control">
                <label>Logo Size:</label>
                <input
                  type="range"
                  min="20"
                  max="200"
                  value={logoSize}
                  onChange={(e) => setLogoSize(Number(e.target.value))}
                />
              </div>
              
              <div className="logo-position-controls">
                <button onClick={() => moveLogoPosition('up')}>↑</button>
                <button onClick={() => moveLogoPosition('down')}>↓</button>
                <button onClick={() => moveLogoPosition('left')}>←</button>
                <button onClick={() => moveLogoPosition('right')}>→</button>
              </div>
            </div>
          )}
        </div>

        {/* Text Controls */}
        <div className="control-section">
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Enter your text"
            className="text-input"
          />
          
          <div className="slider-control">
            <label>Font Size:</label>
            <input
              type="range"
              min="12"
              max="72"
              value={fontSize}
              onChange={(e) => setFontSize(Number(e.target.value))}
            />
          </div>

          <div className="color-control">
            <label>Text Color:</label>
            <input
              type="color"
              value={textColor}
              onChange={(e) => setTextColor(e.target.value)}
            />
          </div>

          <div className="text-align-control">
            <label>Text Align:</label>
            <div className="align-buttons">
              <button 
                className={textAlign === 'left' ? 'active' : ''} 
                onClick={() => setTextAlign('left')}
              >
                <AlignLeft size={20} />
              </button>
              <button 
                className={textAlign === 'center' ? 'active' : ''} 
                onClick={() => setTextAlign('center')}
              >
                <AlignCenter size={20} />
              </button>
              <button 
                className={textAlign === 'right' ? 'active' : ''} 
                onClick={() => setTextAlign('right')}
              >
                <AlignRight size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* Gradient Controls */}
        <div className="control-section">
          <div className="color-control">
            <label>Gradient Color:</label>
            <input
              type="color"
              value={gradientColor}
              onChange={(e) => setGradientColor(e.target.value)}
            />
          </div>

          <div className="slider-control">
            <label>Gradient Opacity:</label>
            <input
              type="range"
              min="0"
              max="100"
              value={gradientOpacity * 100}
              onChange={(e) => setGradientOpacity(Number(e.target.value) / 100)}
            />
          </div>
        </div>

        {/* Download Button */}
        <button className="download-button" onClick={handleDownload}>
          <Download size={20} />
          Download Image
        </button>
      </div>

      {/* Preview */}
      <div className="preview-container">
        {backgroundImage ? (
          <div className="image-preview" ref={editorRef}>
            <img 
              src={backgroundImage} 
              alt="Background" 
              className="background-image"
            />
            <div 
              className="gradient-overlay"
              style={{
                background: `linear-gradient(transparent, ${gradientColor}${Math.round(gradientOpacity * 255).toString(16).padStart(2, '0')})`
              }}
            />
            {logo && (
              <img
                src={logo}
                alt="Logo"
                className="logo-overlay"
                style={{
                  width: `${logoSize}px`,
                  left: `${logoPosition.x}px`,
                  top: `${logoPosition.y}px`
                }}
              />
            )}
            <div 
              className="text-overlay"
              style={{
                color: textColor,
                fontSize: `${fontSize}px`,
                textAlign: textAlign
              }}
            >
              {text}
            </div>
          </div>
        ) : (
          <div className="upload-prompt">
            Upload an image to start
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageEditor;